import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../api/client';
import { useToast } from '../components/Toast';

interface EmployeeHrRow {
  Upn: string;
  DisplayName: string;
  Department?: string | null;
  ManagerUpn?: string | null;
  MonthlySalary?: number | null;
  BonusMultiplier?: number | null;
  StartDate?: string | null;
  EndDate?: string | null;
  SalaryReviewDate?: string | null;
  EmploymentType?: string | null;
  JobTitle?: string | null;
  CostCenter?: string | null;
}

interface HrFormState {
  monthlySalary: number | '';
  bonusMultiplier: number | '';
  startDate: string;
  endDate: string;
  salaryReviewDate: string;
  employmentType: string;
  jobTitle: string;
  costCenter: string;
}

interface TaxBracketRow {
  BracketId?: number;
  MinIncome: number | '';
  MaxIncome: number | '' | null;
  BaseTax: number | '';
  Rate: number | '';
}

const currencyFormat = (val: number) =>
  `R ${val.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const HrDetailsPage: React.FC = () => {
  const api = useApi();
  const { showToast } = useToast();

  const [searchQuery, setSearchQuery] = useState('');
  const [editingUpn, setEditingUpn] = useState<string | null>(null);
  const [formData, setFormData] = useState<HrFormState>({
    monthlySalary: '',
    bonusMultiplier: '',
    startDate: '',
    endDate: '',
    salaryReviewDate: '',
    employmentType: '',
    jobTitle: '',
    costCenter: '',
  });

  const { data: employees, isLoading, error, refetch } = useQuery({
    queryKey: ['hr-employees'],
    queryFn: async () => {
      const data = await api.get('/hr/employees');
      return data as EmployeeHrRow[];
    },
    retry: 2,
    staleTime: 30000,
  });

  const { data: taxRows } = useQuery({
    queryKey: ['tax-brackets'],
    queryFn: async () => {
      const data = await api.get('/hr/tax-brackets');
      return data as TaxBracketRow[];
    },
    retry: 1,
    staleTime: 30000,
  });

  const filtered = useMemo(() => {
    if (!employees) return [];
    if (!searchQuery.trim()) return employees;
    const q = searchQuery.toLowerCase();
    return employees.filter(e =>
      (e.DisplayName || '').toLowerCase().includes(q) ||
      (e.Upn || '').toLowerCase().includes(q) ||
      (e.Department || '').toLowerCase().includes(q) ||
      (e.JobTitle || '').toLowerCase().includes(q),
    );
  }, [employees, searchQuery]);

  const openEdit = (row: EmployeeHrRow) => {
    setEditingUpn(row.Upn);
    setFormData({
      monthlySalary: row.MonthlySalary ?? '',
      bonusMultiplier: row.BonusMultiplier ?? '',
      startDate: row.StartDate ? row.StartDate.substring(0, 10) : '',
      endDate: row.EndDate ? row.EndDate.substring(0, 10) : '',
      salaryReviewDate: row.SalaryReviewDate ? row.SalaryReviewDate.substring(0, 10) : '',
      employmentType: row.EmploymentType || '',
      jobTitle: row.JobTitle || '',
      costCenter: row.CostCenter || '',
    });
  };

  const handleSave = async () => {
    if (!editingUpn) return;
    try {
      await api.put(`/hr/employees/${encodeURIComponent(editingUpn)}`, {
        monthlySalary: formData.monthlySalary === '' ? null : formData.monthlySalary,
        bonusMultiplier: formData.bonusMultiplier === '' ? null : formData.bonusMultiplier,
        startDate: formData.startDate || null,
        endDate: formData.endDate || null,
        salaryReviewDate: formData.salaryReviewDate || null,
        employmentType: formData.employmentType || null,
        jobTitle: formData.jobTitle || null,
        costCenter: formData.costCenter || null,
      });
      showToast('HR details updated', 'success');
      setEditingUpn(null);
      refetch();
    } catch (err: any) {
      showToast(err?.body?.detail || 'Failed to update', 'error');
    }
  };

  const computeTax = (amount: number): number | null => {
    if (!taxRows || taxRows.length === 0) return null;
    const sorted = [...taxRows].sort((a, b) => Number(a.MinIncome) - Number(b.MinIncome));
    for (const row of sorted) {
      const min = Number(row.MinIncome || 0);
      const max = row.MaxIncome === null || row.MaxIncome === '' ? null : Number(row.MaxIncome);
      if (amount >= min && (max === null || amount <= max)) {
        const base = Number(row.BaseTax || 0);
        const rate = Number(row.Rate || 0);
        return Math.round((base + (amount - min) * rate) * 100) / 100;
      }
    }
    return null;
  };

  const annualBonusPreview = () => {
    const salary = formData.monthlySalary === '' ? null : Number(formData.monthlySalary);
    const multiplier = formData.bonusMultiplier === '' ? null : Number(formData.bonusMultiplier);
    if (salary == null || multiplier == null) return null;
    return Math.round(salary * multiplier * 100) / 100;
  };

  return (
    <div>
      <h2>HR Details</h2>
      <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
        Manage monthly salary, bonus multipliers, and onboarding details for employees.
      </p>

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search by name, email, department, or title..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ flex: 1, minWidth: '250px', padding: '8px 12px', fontSize: '14px', border: '1px solid #d1d5db', borderRadius: '4px' }}
        />
        <span style={{ display: 'flex', alignItems: 'center', color: '#6b7280', fontSize: '14px' }}>
          {filtered.length} employee(s)
        </span>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>Loading HR data...</div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#ef4444' }}>
          Failed to load HR details. Make sure you have admin access.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: '14px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                <th style={{ padding: '0.75rem' }}>Employee</th>
                <th style={{ padding: '0.75rem' }}>Job Title</th>
                <th style={{ padding: '0.75rem', textAlign: 'right' }}>Monthly Salary</th>
                <th style={{ padding: '0.75rem', textAlign: 'center' }}>Bonus Multiplier</th>
                <th style={{ padding: '0.75rem', textAlign: 'right' }}>Annual Bonus</th>
                <th style={{ padding: '0.75rem', textAlign: 'right' }}>Net Pay</th>
                <th style={{ padding: '0.75rem' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(row => {
                const salary = row.MonthlySalary ?? null;
                const multiplier = row.BonusMultiplier ?? null;
                const gross = salary != null && multiplier != null ? salary * multiplier : null;
                const tax = gross != null ? computeTax(gross) : null;
                const net = gross != null && tax != null ? gross - tax : null;
                return (
                  <tr key={row.Upn} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem', fontWeight: 500 }}>{row.DisplayName || row.Upn}</td>
                    <td style={{ padding: '0.75rem', color: '#6b7280' }}>{row.JobTitle || '-'}</td>
                    <td style={{ padding: '0.75rem', textAlign: 'right' }}>{salary != null ? currencyFormat(salary) : '-'}</td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>{multiplier != null ? multiplier.toFixed(2) : '-'}</td>
                    <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>
                      {gross != null ? currencyFormat(gross) : '-'}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>
                      {net != null ? currencyFormat(net) : '-'}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <button
                        onClick={() => openEdit(row)}
                        style={{ padding: '4px 12px', fontSize: '13px', border: '1px solid #d1d5db', borderRadius: '4px', background: 'white', color: '#333', cursor: 'pointer' }}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <section style={{ marginTop: '2rem', borderTop: '2px solid #e5e7eb', paddingTop: '1.5rem' }}>
        <h3 style={{ margin: '0 0 0.5rem 0' }}>Monthly PAYE Tax Brackets (2025/2026)</h3>
        <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
          SARS PAYE monthly brackets used to calculate bonus tax.
        </p>
        {!taxRows || taxRows.length === 0 ? (
          <div style={{ padding: '1rem', border: '1px dashed #d1d5db', borderRadius: '8px', color: '#9ca3af' }}>
            No tax brackets configured.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                  <th style={{ padding: '0.5rem 0.75rem' }}>Min Income</th>
                  <th style={{ padding: '0.5rem 0.75rem' }}>Max Income</th>
                  <th style={{ padding: '0.5rem 0.75rem' }}>Base Tax</th>
                  <th style={{ padding: '0.5rem 0.75rem' }}>Rate</th>
                </tr>
              </thead>
              <tbody>
                {taxRows.map((row, idx) => (
                  <tr key={row.BracketId || idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.5rem 0.75rem' }}>{currencyFormat(Number(row.MinIncome || 0))}</td>
                    <td style={{ padding: '0.5rem 0.75rem' }}>
                      {row.MaxIncome == null ? 'No limit' : currencyFormat(Number(row.MaxIncome || 0))}
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem' }}>{currencyFormat(Number(row.BaseTax || 0))}</td>
                    <td style={{ padding: '0.5rem 0.75rem' }}>{(Number(row.Rate || 0) * 100).toFixed(0)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editingUpn && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', color: '#111', padding: '2rem', borderRadius: '8px', maxWidth: '720px', width: '95%', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ marginTop: 0 }}>Edit HR Details</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '0.35rem' }}>Monthly Salary</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.monthlySalary}
                  onChange={(e) => setFormData({ ...formData, monthlySalary: e.target.value === '' ? '' : Number(e.target.value) })}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '0.35rem' }}>Bonus Multiplier</label>
                <input
                  type="number"
                  step="0.001"
                  value={formData.bonusMultiplier}
                  onChange={(e) => setFormData({ ...formData, bonusMultiplier: e.target.value === '' ? '' : Number(e.target.value) })}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '0.35rem' }}>Start Date</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '0.35rem' }}>End Date</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '0.35rem' }}>Salary Review Date</label>
                <input
                  type="date"
                  value={formData.salaryReviewDate}
                  onChange={(e) => setFormData({ ...formData, salaryReviewDate: e.target.value })}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '0.35rem' }}>Employment Type</label>
                <select
                  value={formData.employmentType}
                  onChange={(e) => setFormData({ ...formData, employmentType: e.target.value })}
                >
                  <option value="">Select...</option>
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Contract">Contract</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '0.35rem' }}>Job Title</label>
                <input
                  type="text"
                  value={formData.jobTitle}
                  onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '0.35rem' }}>Cost Center</label>
                <input
                  type="text"
                  value={formData.costCenter}
                  onChange={(e) => setFormData({ ...formData, costCenter: e.target.value })}
                />
              </div>
            </div>

            <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', borderRadius: '8px', background: '#f9fafb', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#374151' }}>
                Bonus Preview
              </div>
              {(() => {
                const gross = annualBonusPreview();
                const tax = gross != null ? computeTax(gross) : null;
                const net = gross != null && tax != null ? gross - tax : null;
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginTop: '0.5rem' }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Gross</div>
                      <div style={{ fontWeight: 700 }}>{gross != null ? currencyFormat(gross) : '-'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>SARS Tax</div>
                      <div style={{ fontWeight: 700 }}>{tax != null ? currencyFormat(tax) : '-'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Net Pay</div>
                      <div style={{ fontWeight: 700 }}>{net != null ? currencyFormat(net) : '-'}</div>
                    </div>
                  </div>
                );
              })()}
              {(!taxRows || taxRows.length === 0) && (
                <div style={{ marginTop: '0.5rem', fontSize: '11px', color: '#9ca3af' }}>
                  Configure the monthly tax table to calculate net pay.
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button
                onClick={() => setEditingUpn(null)}
                style={{ padding: '8px 16px', fontSize: '14px', border: '1px solid #d1d5db', borderRadius: '4px', background: 'white', color: '#333', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                style={{ padding: '8px 16px', fontSize: '14px', border: 'none', borderRadius: '4px', background: '#2563eb', color: 'white', cursor: 'pointer', fontWeight: 600 }}
              >Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HrDetailsPage;
